import React from 'react';
import _ from 'lodash';

import { FieldService } from '../services/Fields';
import LocalStorageService from '../services/utilities/LocalStorageService';
import {
  goChangedFieldPage,
  createFieldsDefaultSearchParams,
  saveFieldData,
} from '../utilities/fieldUtil';
import { messageUtility } from '../utilities/messageUtility';
import _urlParamManager from '../utilities/urlParamManager';
import { getCompanyId } from '../utilities/commonUtil';
import {
  getPermissionByServiceIdAndCompanyId,
  noPermissionDialog,
} from '../utilities/companyUserPermissionUtil';
import dialogLoader from '../utilities/dialogLoader';
import { RadioButtonIcon } from './icons/RadioButtonIcon';
import {
  BUILDEE_SERVICES,
  FIELD_COMPLETE_STATUS,
} from '../utilities/constants';
import SessionStorageService from '../services/utilities/SessionStorageService';

/**
 * 上のヘッダ部分
 *
 * @export
 * @class GlobalHeaderFieldSelect
 * @extends {React.Component}
 */
export class GlobalHeaderFieldSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fieldList: [],
      currentFieldId: SessionStorageService.getFieldId() || null,
      currentFieldName: '未選択',
      currentSelected: {},
      hoverLabel: false,
      fieldLoading: false,
      fieldLoaded: false,
      currentOffset: 0,
      searchKey: '',
      offset: 30,
    };

    document.addEventListener('mousedown', this.handleClickOutside);
    this.loadItem = React.createRef();
    this.scrollBody = React.createRef();
  }

  componentDidMount() {
    this.loadFields();
    this.setFieldNameById(this.state.currentFieldId);
    this.scrollBody.current.addEventListener(
      'scroll',
      _.throttle(e => this.handleInview(e, this.loadFields), 1000)
    );
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = e => {
    if (!this.node.contains(e.target)) {
      this.setState({ hoverLabel: false });
    }
  };

  toggleFieldSelect = () => {
    const { hoverLabel } = this.state;
    this.setState({
      hoverLabel: !hoverLabel,
    });
  };

  setFieldNameById(fieldId) {
    if (!fieldId) {
      return;
    }

    // フィールドの情報を取得する
    FieldService.info(fieldId).then(response => {
      if (response.result) {
        this.setState({
          // 現場名
          currentFieldName: response.data.entities.field.name,
          // リロードで消えてしまうのでfieldを入れる
          currentSelected: response.data.entities.field,
        });
      }
    });
  }

  handleSearchKeyChange = e => {
    const setStateValue = {
      searchKey: String(e.target.value),
      currentOffset: 0,
      fieldLoaded: false,
      fieldList: [],
    };

    this.setState(setStateValue, () => {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.intervalId = setInterval(() => {
          if (this.state.fieldLoading === true) {
            return;
          }
          clearInterval(this.intervalId);
          this.setState(setStateValue, () => {
            this.loadFields();
          });
        }, 100);
      }, 1000);
    });
  };

  handleFieldSelect = (e, field) => {
    // 選択時にセットしているが画面がリロードされて消えてしまうので意味ない
    this.setState({ currentSelected: field, hoverLabel: false }, () => {
      this.handleChangeField();
    });
  };

  /**
   * ヘッダのプルダウンの現場を選択時
   *
   * @memberof GlobalHeaderFieldSelect
   */
  async handleChangeField() {
    const { id: fieldId } = this.state.currentSelected;

    if (!fieldId) {
      return;
    }

    const serviceId = _urlParamManager.getCurrentServiceTypeId();
    const companyId = getCompanyId();
    const permission = await getPermissionByServiceIdAndCompanyId(
      serviceId,
      companyId
    );

    if (!permission) {
      dialogLoader.show(noPermissionDialog, {
        callback: {
          done: () => {
            dialogLoader.close();
          },
        },
      });
      this.setState({ currentSelected: {} });
      return;
    }

    const result = await saveFieldData(
      fieldId,
      LocalStorageService.getRoleId()
    );
    if (result) {
      // 更新した値が戻ってしまうので、同期的に行う
      goChangedFieldPage(_urlParamManager.getCurrentServiceTypeId());
    }

    // TODO 1末リリース以降現場の遷移に問題なければ下記処理は削除
    // await changeFieldWriteStorage(this.state.currentSelected);
    //
    // // リロードするので必要ないのでコメントアウト
    // // 後々リロードしなくても良いようにするなら必要かもしれないなので残しておく
    // //this.setState({ currentFieldId: fieldId });
    // //this.setFieldNameById(fieldId);
    //
    // //現場情報更新（共通API：他システムとの連携用）
    // SessionsService.updatefield(fieldId).then(resp => {
    //   if (!resp.result) {
    //     console.error('選択現場の更新に失敗しました');
    //   } else {
    //     // 更新した値が戻ってしまうので、同期的に行う
    //     goChangedFieldPage();
    //   }
    // });
  }

  handleInview = (e, callback) => {
    if (this.state.fieldLoading || this.state.fieldLoaded) {
      return;
    }
    const scrollBody = e.target;
    const scrollBottom = scrollBody.scrollTop + scrollBody.offsetHeight;
    if (scrollBottom > scrollBody.scrollHeight - 22) {
      callback();
    }
  };

  loadFields = () => {
    this.setState({ fieldLoading: true }, () => {
      const { currentOffset, searchKey } = this.state;
      const searchKeyParam = searchKey !== '' ? { name: searchKey } : {};
      const loadListParam = {
        ...createFieldsDefaultSearchParams(),
        ...searchKeyParam,
        offset: currentOffset,
        usage_status: [
          FIELD_COMPLETE_STATUS.RUNNING.ID,
          FIELD_COMPLETE_STATUS.COMPLETED.ID,
        ],
      };

      const path = window.location.pathname.split('/');
      if (path[1] === 'gatekeeper') {
        loadListParam.use_gk_flg = 1;
      }
      if (_.isEqual(path[1], BUILDEE_SERVICES.PROGRESS.KEYWORD)) {
        loadListParam.use_progress_flg = 1;
      } else if (_.isEqual(path[1], BUILDEE_SERVICES.TRACKER.KEYWORD)) {
        loadListParam.use_tracker_flg = 1;
      }

      FieldService.list(loadListParam).then(response => {
        if (response.result) {
          const { entities } = response.data;
          const { offset, currentOffset, fieldList: loadedFields } = this.state;
          const hasNext = _.size(entities.fields) < offset;
          const fieldList = _.concat(loadedFields, entities.fields);
          const newOffset = currentOffset + offset;
          this.setState({
            fieldList,
            fieldLoaded: hasNext,
            fieldLoading: false,
            currentOffset: newOffset,
          });
        }
      });
    });
  };

  renderFieldItem = field => {
    const { id, name } = field;
    const { currentSelected } = this.state;

    return (
      <li className='single-select__item' key={id}>
        <label className='radio' htmlFor={`global-header-list-${id}`}>
          <input
            id={`global-header-list-${id}`}
            type='radio'
            name='global-header-list'
            value={id}
            checked={currentSelected.id === id}
            onChange={e => this.handleFieldSelect(e, field)}
          />
          <RadioButtonIcon className='icon--1_6x' />
          <span className='single-select__label-text'>{name}</span>
        </label>
      </li>
    );
  };

  render = () => {
    return (
      <div
        ref={node => (this.node = node)}
        style={{ display: 'block', width: '100%' }}
      >
        <div className='dropdown-single-select__header'>
          <button className='field-btn-header' onClick={this.toggleFieldSelect}>
            <span className='field-btn-header__text'>
              {this.state.currentFieldName}
            </span>
            <i className='i-arrow-bottom--light i-3x' aria-hidden='true' />
          </button>
        </div>
        <div
          id='construction-list-dropdown'
          className={`dropdown dropdown--dropdown-single-select${
            this.state.hoverLabel ? ' show' : ''
          }`}
        >
          <div className='dropdown__item dropdown__item--wide'>
            <div className='dropdown-single-select__item'>
              <i className='i-search i-2x' />
              <input
                type='text'
                placeholder={messageUtility.getMessage('I-000-090')}
                value={this.state.searchKey}
                onChange={this.handleSearchKeyChange}
              />
            </div>
            <div
              className='dropdown-single-select__scroll'
              ref={this.scrollBody}
              id='global-header-field-select-list'
            >
              <ul className='single-select'>
                {_.map(this.state.fieldList, field => {
                  return this.renderFieldItem(field);
                })}
                {this.state.fieldLoading || this.state.fieldLoaded ? null : (
                  <li className='single-select__item' ref={this.loadItem}>
                    ...
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };
}



// WEBPACK FOOTER //
// ./src/components/GlobalHeaderFieldSelect.jsx